import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  Loader2,
  Package,
  CheckCircle2,
  Truck,
  ShoppingCart,
  ChevronLeft,
  ChevronRight,
  MapPin,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [shippingInfo, setShippingInfo] = useState(null);
  const [checkingShipping, setCheckingShipping] = useState(false);
  const [extrasPricing, setExtrasPricing] = useState({ silicone: 0, wedges: 0, brackets: 0 });
  const [selectedExtras, setSelectedExtras] = useState({ silicone: false, wedges: false, brackets: false });
  
  const [orderForm, setOrderForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    delivery_address: "",
    delivery_postcode: "",
    notes: "",
  });

  useEffect(() => {
    fetchProduct();
    fetchExtrasPricing();
  }, [productId]);

  const fetchExtrasPricing = async () => {
    try {
      const res = await axios.get(`${API}/extras-pricing`);
      setExtrasPricing(res.data);
    } catch (error) {
      console.error("Failed to fetch extras pricing:", error);
    }
  };

  useEffect(() => {
    if (orderForm.delivery_postcode && orderForm.delivery_postcode.length >= 2) {
      const timer = setTimeout(() => {
        checkShipping(orderForm.delivery_postcode);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [orderForm.delivery_postcode]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/products/${productId}`);
      setProduct(res.data);
    } catch (error) {
      console.error("Failed to fetch product:", error);
      if (error.response?.status === 404) {
        navigate("/shop");
      }
    } finally {
      setLoading(false);
    }
  };

  const checkShipping = async (postcode) => {
    setCheckingShipping(true);
    try {
      const response = await axios.post(`${API}/shipping/calculate`, { postcode });
      setShippingInfo(response.data);
    } catch (error) {
      console.error("Shipping check error:", error);
    } finally {
      setCheckingShipping(false);
    }
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    if (!product) return;

    setSubmitting(true);
    try {
      const extras = Object.entries(selectedExtras).filter(([, v]) => v).map(([k]) => k);
      await axios.post(`${API}/product-orders`, {
        product_id: product.id,
        quantity: quantity,
        extras: extras,
        customer_name: orderForm.customer_name,
        customer_email: orderForm.customer_email,
        customer_phone: orderForm.customer_phone,
        delivery_address: orderForm.delivery_address,
        delivery_postcode: orderForm.delivery_postcode,
        notes: orderForm.notes,
      });
      
      toast.success("Order submitted! We'll be in touch shortly to arrange payment and delivery.");
      setShowOrderModal(false);
      setOrderForm({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        delivery_address: "",
        delivery_postcode: "",
        notes: "",
      });
    } catch (error) {
      toast.error("Failed to submit order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const nextImage = () => {
    if (product?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
    }
  };

  const prevImage = () => {
    if (product?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
    }
  };

  const hasDiscount = product?.sale_price && product.sale_price < product.price;
  const displayPrice = hasDiscount ? product.sale_price : product?.price;
  const extrasCost = Object.entries(selectedExtras).reduce((sum, [key, checked]) => checked ? sum + (extrasPricing[key] || 0) : sum, 0);
  const totalPrice = displayPrice ? (displayPrice * quantity) + extrasCost : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0F3D2E]" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-[#57534E] mx-auto mb-4" />
          <h2 className="text-xl font-medium mb-4">Product Not Found</h2>
          <Link to="/shop">
            <Button className="btn-primary">Back to Shop</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      {/* Navigation */}
      <nav className="nav-wrapper" data-testid="product-nav">
        <div className="container-padding py-4 flex items-center justify-between max-w-7xl mx-auto">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#0F3D2E] flex items-center justify-center">
              <span className="text-white font-semibold text-lg">S</span>
            </div>
            <span className="font-semibold text-lg tracking-tight">
              Stone Sills Direct
            </span>
          </Link>

          <Link
            to="/shop"
            className="flex items-center gap-2 text-sm text-[#57534E] hover:text-[#0F3D2E]"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Shop
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container-padding max-w-7xl mx-auto py-12" data-testid="product-detail">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Images */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="relative aspect-square bg-[#F5F5F4] overflow-hidden">
              {product.images && product.images.length > 0 ? (
                <>
                  <img
                    src={product.images[currentImageIndex].url}
                    alt={product.images[currentImageIndex].alt || product.name}
                    className="w-full h-full object-cover"
                  />
                  {product.images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 flex items-center justify-center hover:bg-white"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 flex items-center justify-center hover:bg-white"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Package className="w-24 h-24 text-[#D6D3D1]" />
                </div>
              )}
              
              {/* Badges */}
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                {product.featured && (
                  <span className="px-3 py-1 bg-[#C27C2E] text-white text-sm uppercase">
                    Featured
                  </span>
                )}
                {hasDiscount && (
                  <span className="px-3 py-1 bg-red-600 text-white text-sm uppercase">
                    Sale
                  </span>
                )}
              </div>
            </div>

            {/* Thumbnail Gallery */}
            {product.images && product.images.length > 1 && (
              <div className="flex gap-3 mt-4 overflow-x-auto">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`w-20 h-20 flex-shrink-0 border-2 ${
                      idx === currentImageIndex ? 'border-[#0F3D2E]' : 'border-transparent'
                    }`}
                  >
                    <img
                      src={img.url}
                      alt={img.alt || `${product.name} ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="sticky top-24">
              <h1 className="text-3xl font-medium mb-4">{product.name}</h1>
              
              {product.shelf_dimensions && (
                <p className="text-[#57534E] mb-4">{product.shelf_dimensions}</p>
              )}

              {/* Price */}
              <div className="flex items-baseline gap-3 mb-6">
                {hasDiscount ? (
                  <>
                    <span className="text-3xl font-medium text-[#0F3D2E]">
                      £{product.sale_price.toFixed(2)}
                    </span>
                    <span className="text-xl text-[#57534E] line-through">
                      £{product.price.toFixed(2)}
                    </span>
                  </>
                ) : (
                  <span className="text-3xl font-medium text-[#0F3D2E]">
                    £{product.price.toFixed(2)}
                  </span>
                )}
              </div>

              {/* Stock Status */}
              <div className="mb-6">
                {product.stock_status === 'in_stock' && (
                  <span className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="w-5 h-5" />
                    In Stock
                  </span>
                )}
                {product.stock_status === 'low_stock' && (
                  <span className="flex items-center gap-2 text-amber-600">
                    <CheckCircle2 className="w-5 h-5" />
                    Low Stock - Order Soon
                  </span>
                )}
                {product.stock_status === 'out_of_stock' && (
                  <span className="flex items-center gap-2 text-gray-600">
                    Out of Stock
                  </span>
                )}
              </div>

              {/* Description */}
              <div className="prose prose-stone mb-6">
                <p className="text-[#57534E] whitespace-pre-line">{product.description}</p>
              </div>

              {/* What's Included */}
              {product.includes && product.includes.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-medium mb-3">Kit Includes:</h3>
                  <ul className="space-y-2">
                    {product.includes.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-[#57534E]">
                        <CheckCircle2 className="w-4 h-4 text-[#0F3D2E]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Bracket Info */}
              {(product.bracket_type || product.bracket_colour) && (
                <div className="mb-6 p-4 bg-[#F5F5F4]">
                  <h3 className="font-medium mb-2">Bracket Details</h3>
                  {product.bracket_type && (
                    <p className="text-sm text-[#57534E]">Type: {product.bracket_type}</p>
                  )}
                  {product.bracket_colour && (
                    <p className="text-sm text-[#57534E]">Colour: {product.bracket_colour}</p>
                  )}
                </div>
              )}

              {/* Quantity & Order */}
              {product.stock_status !== 'out_of_stock' && (
                <div className="space-y-4">
                  {/* Extras */}
                  <div className="p-4 bg-[#F5F5F4]">
                    <h3 className="font-medium mb-3">Add Extras</h3>
                    <div className="space-y-3">
                      {[
                        { key: 'silicone', label: 'Silicone' },
                        { key: 'wedges', label: 'Wedges' },
                        { key: 'brackets', label: 'Brackets' },
                      ].map(({ key, label }) => (
                        <label key={key} className="flex items-center justify-between cursor-pointer group" data-testid={`extra-${key}`}>
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={selectedExtras[key]}
                              onChange={(e) => setSelectedExtras(prev => ({ ...prev, [key]: e.target.checked }))}
                              className="w-4 h-4 accent-[#0F3D2E]"
                            />
                            <span className="text-sm group-hover:text-[#0F3D2E]">{label}</span>
                          </div>
                          <span className="text-sm font-medium text-[#0F3D2E]">+ £{extrasPricing[key]?.toFixed(2)}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <Label className="text-sm font-medium">Quantity:</Label>
                    <div className="flex items-center">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="w-10 h-10 border border-[#E7E5E4] flex items-center justify-center hover:bg-[#F5F5F4]"
                      >
                        -
                      </button>
                      <span className="w-14 h-10 border-t border-b border-[#E7E5E4] flex items-center justify-center">
                        {quantity}
                      </span>
                      <button
                        onClick={() => setQuantity(quantity + 1)}
                        className="w-10 h-10 border border-[#E7E5E4] flex items-center justify-center hover:bg-[#F5F5F4]"
                      >
                        +
                      </button>
                    </div>
                    <span className="text-lg font-medium text-[#0F3D2E]">
                      Total: £{totalPrice.toFixed(2)}
                    </span>
                  </div>

                  <Button
                    className="btn-primary w-full"
                    onClick={() => setShowOrderModal(true)}
                    data-testid="order-product-btn"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Order Now
                  </Button>

                  <div className="flex items-center gap-2 text-sm text-[#57534E]">
                    <Truck className="w-4 h-4" />
                    Courier delivery available
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Order Modal */}
      <Dialog open={showOrderModal} onOpenChange={setShowOrderModal}>
        <DialogContent className="sm:max-w-md" data-testid="product-order-modal">
          <DialogHeader>
            <DialogTitle>Order {product.name}</DialogTitle>
          </DialogHeader>
          
          <div className="mb-4 p-3 bg-[#F5F5F4]">
            <p className="text-sm text-[#57534E]">
              {quantity} × £{displayPrice?.toFixed(2)}
            </p>
            {Object.entries(selectedExtras).some(([, v]) => v) && (
              <div className="text-sm text-[#57534E] mt-1">
                {Object.entries(selectedExtras).filter(([, v]) => v).map(([key]) => (
                  <span key={key} className="mr-3">+ {key.charAt(0).toUpperCase() + key.slice(1)} £{extrasPricing[key]?.toFixed(2)}</span>
                ))}
              </div>
            )}
            <p className="font-medium mt-1">Total: £{totalPrice.toFixed(2)} <span className="text-xs text-[#57534E] font-normal">ex VAT</span></p>
          </div>

          <form onSubmit={handleSubmitOrder} className="space-y-4">
            <div>
              <Label className="input-label">Your Name</Label>
              <Input
                required
                value={orderForm.customer_name}
                onChange={(e) => setOrderForm((prev) => ({ ...prev, customer_name: e.target.value }))}
                className="input-field"
                data-testid="order-name"
              />
            </div>
            <div>
              <Label className="input-label">Email Address</Label>
              <Input
                type="email"
                required
                value={orderForm.customer_email}
                onChange={(e) => setOrderForm((prev) => ({ ...prev, customer_email: e.target.value }))}
                className="input-field"
                data-testid="order-email"
              />
            </div>
            <div>
              <Label className="input-label">Phone Number</Label>
              <Input
                type="tel"
                required
                value={orderForm.customer_phone}
                onChange={(e) => setOrderForm((prev) => ({ ...prev, customer_phone: e.target.value }))}
                className="input-field"
                data-testid="order-phone"
              />
            </div>
            <div>
              <Label className="input-label">Delivery Address</Label>
              <Textarea
                required
                value={orderForm.delivery_address}
                onChange={(e) => setOrderForm((prev) => ({ ...prev, delivery_address: e.target.value }))}
                className="input-field min-h-[80px]"
                placeholder="Full delivery address"
                data-testid="order-address"
              />
            </div>
            <div>
              <Label className="input-label">Delivery Postcode</Label>
              <div className="relative">
                <Input
                  required
                  value={orderForm.delivery_postcode}
                  onChange={(e) => setOrderForm((prev) => ({ ...prev, delivery_postcode: e.target.value.toUpperCase() }))}
                  className="input-field"
                  placeholder="e.g. OX11 7HH"
                  data-testid="order-postcode"
                />
                {checkingShipping && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin" />
                )}
              </div>
              {shippingInfo && shippingInfo.price > 0 && (
                <p className="text-sm mt-1 text-[#0F3D2E]">
                  <MapPin className="w-3 h-3 inline mr-1" />
                  Delivery: £{shippingInfo.price.toFixed(2)}
                </p>
              )}
            </div>
            <div>
              <Label className="input-label">Notes (Optional)</Label>
              <Textarea
                value={orderForm.notes}
                onChange={(e) => setOrderForm((prev) => ({ ...prev, notes: e.target.value }))}
                className="input-field min-h-[60px]"
                placeholder="Any special instructions..."
                data-testid="order-notes"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="btn-secondary flex-1"
                onClick={() => setShowOrderModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn-primary flex-1"
                disabled={submitting}
                data-testid="confirm-product-order-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Order"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductDetail;
